import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Home, ShoppingBag, Grid3x3, Heart, Package, Info, Mail, HelpCircle, FileText, Shield, ScrollText } from 'lucide-react';
import { useStoreInfo } from '@/lib/store';
import { SocialMediaLinks } from '@/components/ui/social-media-links';

interface MobileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export function MobileDrawer({ isOpen, onClose }: MobileDrawerProps) {
  const location = useLocation();
  const { storeInfo } = useStoreInfo();

  const mainNavigation = [
    { name: 'Home', href: '/home', icon: Home },
    { name: 'Products', href: '/products', icon: ShoppingBag },
    { name: 'Categories', href: '/categories', icon: Grid3x3 },
    { name: 'Wishlist', href: '/wishlist', icon: Heart },
    { name: 'Track Order', href: '/track', icon: Package },
  ];

  const supportLinks = [
    { name: 'About Us', href: '/about', icon: Info },
    { name: 'Contact Us', href: '/contact', icon: Mail },
    { name: 'FAQ', href: '/faq', icon: HelpCircle },
    { name: 'Return Policy', href: '/return-policy', icon: FileText },
    { name: 'Privacy Policy', href: '/privacy-policy', icon: Shield },
    { name: 'Terms of Service', href: '/terms-of-service', icon: ScrollText },
  ];

  const socialLinks = storeInfo?.social || {
    facebook: storeInfo?.shop_fb || '',
    twitter: storeInfo?.shop_x || '',
    instagram: storeInfo?.shop_ig || '',
    tiktok: storeInfo?.tiktok_url || '',
    whatsapp: storeInfo?.shop_wa || '',
    telegram: storeInfo?.shop_tg || ''
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-40"
          />

          {/* Drawer - Slides from left to right */}
          <motion.div
            initial={{ x: -100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -100, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed left-0 top-0 bottom-0 z-50 w-80 bg-white shadow-2xl overflow-y-auto"
          >
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-r from-brand-600 to-brand-500 p-6 flex items-center justify-between">
              <div className="flex-1">
                <h2 className="text-white font-display font-bold text-xl">Menu</h2>
                {storeInfo?.slogan && (
                  <p className="text-brand-50 text-sm font-light mt-1">{storeInfo.slogan}</p>
                )}
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors flex-shrink-0"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Navigation Content */}
            <div className="p-4 space-y-5">
              {/* Main Navigation */}
              <nav className="space-y-1.5">
                {mainNavigation.map((item, idx) => (
                  <motion.div
                    key={item.name}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05 }}
                  >
                    <Link
                      to={item.href}
                      onClick={onClose}
                      className={`flex items-center gap-3 px-4 py-3.5 rounded-xl font-semibold transition-all duration-200 group ${
                        isActive(item.href)
                          ? 'bg-gradient-to-r from-brand-500 to-brand-600 text-white shadow-lg shadow-brand-500/30'
                          : 'text-gray-700 hover:bg-gray-100 hover:text-brand-600'
                      }`}
                    >
                      <div className={`p-2.5 rounded-lg transition-all duration-200 ${
                        isActive(item.href) 
                          ? 'bg-white/25 text-white' 
                          : 'bg-gray-200 text-gray-600 group-hover:bg-brand-100 group-hover:text-brand-600'
                      }`}>
                        <item.icon className="h-5 w-5" />
                      </div>
                      <span className="flex-1">{item.name}</span>
                      {isActive(item.href) && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="w-2 h-2 rounded-full bg-white"
                        />
                      )}
                    </Link>
                  </motion.div>
                ))}
              </nav>

              {/* Divider */}
              <div className="h-px bg-gradient-to-r from-brand-200 via-brand-300 to-brand-200 my-4" />

              {/* Support Links */}
              <div>
                <h3 className="px-4 mb-3.5 text-xs font-extrabold text-brand-700 uppercase tracking-widest">
                  Support & Info
                </h3>
                <nav className="space-y-1.5">
                  {supportLinks.map((item, idx) => (
                    <motion.div
                      key={item.name}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.25 + idx * 0.04 }}
                    >
                      <Link
                        to={item.href}
                        onClick={onClose}
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl font-medium text-sm transition-all duration-200 group ${
                          isActive(item.href)
                            ? 'bg-gradient-to-r from-brand-500 to-brand-600 text-white shadow-lg shadow-brand-500/30'
                            : 'text-gray-600 hover:bg-gray-100 hover:text-brand-600'
                        }`}
                      >
                        <div className={`p-2 rounded-lg transition-all duration-200 flex-shrink-0 ${
                          isActive(item.href)
                            ? 'bg-white/25 text-white'
                            : 'bg-gray-200 text-gray-500 group-hover:bg-brand-100 group-hover:text-brand-600'
                        }`}>
                          <item.icon className="h-4 w-4" />
                        </div>
                        <span className="flex-1">{item.name}</span>
                        {isActive(item.href) && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="w-1.5 h-1.5 rounded-full bg-white"
                          />
                        )}
                      </Link>
                    </motion.div>
                  ))}
                </nav>
              </div>

              {/* Contact Info */}
              {(storeInfo?.contact_email || storeInfo?.phone_num) && (
                <>
                  <div className="h-px bg-gradient-to-r from-brand-200 via-brand-300 to-brand-200 my-4" />
                  <div className="bg-gradient-to-br from-brand-50 to-brand-100/50 rounded-xl p-4 border border-brand-200/50">
                    <div className="space-y-2.5">
                      {storeInfo?.phone_num && (
                        <p className="text-xs sm:text-sm text-gray-700">
                          <span className="font-bold text-brand-700">Phone:</span>
                          <span className="ml-2 text-gray-600">{storeInfo.phone_num}</span>
                        </p>
                      )}
                      {storeInfo?.contact_email && (
                        <p className="text-xs sm:text-sm text-gray-700">
                          <span className="font-bold text-brand-700">Email:</span>
                          <span className="ml-2 text-gray-600">{storeInfo.contact_email}</span>
                        </p>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Social Media Links */}
              <div>
                <div className="h-px bg-gradient-to-r from-brand-200 via-brand-300 to-brand-200 my-4" />
                <h3 className="px-4 mb-4 text-xs font-extrabold text-brand-700 uppercase tracking-widest">
                  Follow Us
                </h3>
                <div className="flex justify-center">
                  <SocialMediaLinks socialLinks={socialLinks} size="md" variant="icon-only" theme="light" />
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
